import { useEffect, useState } from 'react';
import { TrendingUp, Users, Clock, ChevronRight, CheckCircle } from 'lucide-react';
import { apiFetch } from '../lib/api';
import type { Donation } from '../types';
import { useNavigate } from 'react-router-dom';

interface DashboardData {
  totalDonated: number;
  totalDonationCount: number;
  confirmedTotal: number;
  confirmedCount: number;
  pendingTotal: number;
  pendingCount: number;
  todayDonations: number;
  todayAmount: number;
  weekDonations: number;
  weekAmount: number;
  totalDonors: number;
  monthlyDonors: number;
  recentDonations: Donation[];
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await apiFetch<DashboardData>('/api/admin/dashboard');
      if (res.success && res.data) {
        setData(res.data);
      }
      setLoading(false);
    })();
  }, []);

  const kpiCards: {
    label: string;
    value: string | number;
    sub?: string;
    icon: React.ElementType;
    color: string;
    bg: string;
  }[] = [
    {
      label: '總金額',
      value: data ? `HK$${(data.totalDonated / 100).toLocaleString()}` : '—',
      icon: TrendingUp,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      label: '已核實',
      value: data ? `HK$${(data.confirmedTotal / 100).toLocaleString()}` : '—',
      sub: data ? `${data.confirmedCount} 筆` : undefined,
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      label: '待核實',
      value: data ? `HK$${(data.pendingTotal / 100).toLocaleString()}` : '—',
      sub: data ? `${data.pendingCount} 筆` : undefined,
      icon: Clock,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      label: '月捐人數',
      value: data?.monthlyDonors ?? 0,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
  ];

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-amber-100 text-amber-700',
      confirmed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status === 'pending' && '待確認'}
        {status === 'confirmed' && '已確認'}
        {status === 'failed' && '失敗'}
      </span>
    );
  };

  const methodLabel = (m: string) => {
    const labels: Record<string, string> = {
      fps: 'FPS',
      payme: 'PayMe',
      'bank-transfer': '銀行轉賬',
      'credit-card': '信用卡',
      other: '其他',
    };
    return labels[m] || m;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">儀表板</h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card) => (
          <div key={card.label} className="bg-white rounded-2xl shadow-sm border border-brand-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-500">{card.label}</span>
              <div className={`w-9 h-9 ${card.bg} rounded-lg flex items-center justify-center`}>
                <card.icon className={`w-5 h-5 ${card.color}`} />
              </div>
            </div>
            <div className="text-2xl font-extrabold text-gray-900">
              {loading ? (
                <span className="inline-block w-16 h-7 bg-gray-100 rounded animate-pulse" />
              ) : (
                card.value
              )}
            </div>
            {card.sub && (
              <div className="text-xs text-gray-400 mt-1">{card.sub}</div>
            )}
          </div>
        ))}
      </div>

      {/* Quick stats row */}
      {!loading && data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-5">
            <div className="text-sm text-gray-500 mb-1">今日捐款</div>
            <div className="text-xl font-bold text-gray-900">HK${(data.todayAmount / 100).toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-5">
            <div className="text-sm text-gray-500 mb-1">本週捐款</div>
            <div className="text-xl font-bold text-gray-900">HK${(data.weekAmount / 100).toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Recent donations */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-gray-900">最近捐款</h2>
          <button
            onClick={() => navigate('/admin/donations')}
            className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1 font-medium"
          >
            查看全部
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-6 py-3 text-left font-medium">編號</th>
                <th className="px-6 py-3 text-left font-medium">姓名</th>
                <th className="px-6 py-3 text-right font-medium">金額</th>
                <th className="px-6 py-3 text-left font-medium">方式</th>
                <th className="px-6 py-3 text-left font-medium">狀態</th>
                <th className="px-6 py-3 text-left font-medium">日期</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-6 py-3">
                        <span className="inline-block w-full h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data?.recentDonations && data.recentDonations.length > 0 ? (
                data.recentDonations.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 font-mono text-gray-600">{d.donationNum}</td>
                    <td className="px-6 py-3 text-gray-800">
                      {d.isAnonymous ? '匿名' : d.donorName}
                    </td>
                    <td className="px-6 py-3 text-right font-semibold text-gray-900">
                      HK${(d.amount / 100).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-gray-600">{methodLabel(d.paymentMethod)}</td>
                    <td className="px-6 py-3">{statusBadge(d.paymentStatus)}</td>
                    <td className="px-6 py-3 text-gray-500">
                      {new Date(d.createdAt).toLocaleDateString('zh-HK')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-400">
                    暫無捐款記錄
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
